import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orpc, rpcClient } from 'src/api/rpc/client';
import type {
  CreateTaskPageInput,
  ListTaskPagesInput,
  UpdateTaskPageInput,
} from '@erp/rpc';

/**
 * Hooks over org-configurable task pages (`taskPages.*`). The default
 * 'visible' view powers the sidebar (audience-filtered); the 'manage' view
 * powers the settings admin card. Mutations invalidate both.
 */

function useInvalidateTaskPages() {
  const queryClient = useQueryClient();
  return function invalidateTaskPages() {
    void queryClient.invalidateQueries({
      queryKey: orpc.taskPages.list.key(),
    });
    void queryClient.invalidateQueries({
      queryKey: orpc.taskPages.get.key(),
    });
  };
}

export function useTaskPages(options?: {
  view?: ListTaskPagesInput['view'];
  enabled?: boolean;
}) {
  return useQuery({
    ...orpc.taskPages.list.queryOptions({
      input: { view: options?.view ?? 'visible' },
    }),
    staleTime: 5 * 60 * 1000,
    enabled: options?.enabled ?? true,
  });
}

/**
 * Polling cadence for the sidebar count badges while the tab is visible —
 * the same 30s the notification bell uses. Task mutations also invalidate
 * this key (see invalidateTaskQueries) so completing a task from a page
 * updates its badge without waiting for the next tick.
 */
const TASK_PAGE_COUNTS_POLL_INTERVAL_MS = 30_000;

export function useTaskPageCounts(options?: { enabled?: boolean }) {
  return useQuery({
    ...orpc.taskPages.counts.queryOptions(),
    enabled: options?.enabled ?? true,
    refetchInterval: TASK_PAGE_COUNTS_POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
  });
}

export function useTaskPage(id: string | null | undefined) {
  return useQuery({
    ...orpc.taskPages.get.queryOptions({ input: { id: id ?? '' } }),
    enabled: !!id,
  });
}

export function useCreateTaskPage() {
  const invalidateTaskPages = useInvalidateTaskPages();
  return useMutation({
    mutationFn: (input: CreateTaskPageInput) =>
      rpcClient.taskPages.create(input),
    onSuccess: invalidateTaskPages,
  });
}

export function useUpdateTaskPage() {
  const invalidateTaskPages = useInvalidateTaskPages();
  return useMutation({
    mutationFn: (input: UpdateTaskPageInput) =>
      rpcClient.taskPages.update(input),
    onSuccess: invalidateTaskPages,
  });
}

export function useArchiveTaskPage() {
  const invalidateTaskPages = useInvalidateTaskPages();
  return useMutation({
    mutationFn: ({ id }: { id: string }) => rpcClient.taskPages.archive({ id }),
    onSuccess: invalidateTaskPages,
  });
}
